import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Footer from "../HomePage/Footer";
import { useMediaQuery } from "react-responsive";
import { API_METHODS, BLANK_VALUE } from "../../../constants";
import useApi from "../../../hooks/useApi";
import {
  Row,
  Col,
  Card,
  Layout,
  Menu,
  Divider,
  Drawer,
  Button,
  Spin,
} from "antd";
import { MenuOutlined } from "@ant-design/icons";
import Banner from "../Banner";
import Sider from "antd/es/layout/Sider";
import { Content } from "antd/es/layout/layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { theme } from "../../../assets/theme";
import productDemoImage from "../../../assets/img/homepage/applications/Applications7.png";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import { formatBreadcrumbText } from "../../../generalFunctions";

function CategoryPage() {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [categories, setCategories] = useState([]);
  const [banner, setBanner] = useState();
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState();
  const [currentSelectedCategory, setCurrentSelectedCategory] = useState();
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [categorySEO, setCategorySEO] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const navigate = useNavigate();
  const { main_category_url, category_url } = useParams();
  const location = useLocation();

  const subcategoryClicked = (subcategory) => {
    let url = subcategory.url_slug;
    navigate(location.pathname + `/${url}`);
  };
  const [bannerResponse, getBannerRequest] = useApi(
    `/website/category-page/${main_category_url}/banner`,
    null,
    API_METHODS.GET
  );

  const [categoryResponse, getCategoryRequest] = useApi(
    `/website/category-page/${main_category_url}/category`,
    null,
    API_METHODS.GET
  );

  const [subcategoryResponse, getSubcategoryRequest] = useApi(
    `/website/category-page/${category_url}/subcategory?pageNumber=1`,
    null,
    API_METHODS.GET
  );

  // Generate SEO data with smart fallbacks
  const generateSEOData = (categoryInfo, categorySEO) => {
    if (!categoryInfo) return {};

    const baseUrl = `https://cenvance.com/${categoryInfo.main_category_url_slug}/${categoryInfo.url_slug}`;
    return {
      title:
        categorySEO?.meta_title ||
        `${categoryInfo.category} - ${categoryInfo.main_category} | Your Company`,

      description:
        categorySEO?.meta_description ||
        `Explore our comprehensive range of ${categoryInfo.category.toLowerCase()} products. High-quality ${categoryInfo.main_category.toLowerCase()} equipment for professional use.`,

      keywords:
        categorySEO?.meta_keywords ||
        `${categoryInfo.category}, ${categoryInfo.main_category}, equipment, professional`,

      canonicalUrl: categorySEO?.canonical_url || baseUrl,

      robots: categorySEO?.meta_robots || "index, follow",

      ogTitle:
        categorySEO?.og_title ||
        categorySEO?.meta_title ||
        categoryInfo.category,

      ogDescription:
        categorySEO?.og_description ||
        categorySEO?.meta_description ||
        `Browse ${categoryInfo.category} products from Your Company`,

      ogImage: categorySEO?.og_image || "/default-category-og-image.jpg",

      twitterTitle:
        categorySEO?.twitter_title ||
        categorySEO?.og_title ||
        categorySEO?.meta_title ||
        categoryInfo.category,

      twitterDescription:
        categorySEO?.twitter_description ||
        categorySEO?.og_description ||
        categorySEO?.meta_description ||
        `Explore ${categoryInfo.category} products`,

      twitterImage:
        categorySEO?.twitter_image ||
        categorySEO?.og_image ||
        "/default-category-og-image.jpg",

      breadcrumbName: categorySEO?.breadcrumb_name || categoryInfo.category,
    };
  };

  // Generate structured data for category
  const generateCategorySchema = (categoryInfo, seoData) => {
    if (!categoryInfo) return null;

    const baseSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: seoData.title,
      description: seoData.description,
      url: seoData.canonicalUrl,
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://cenvance.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: categoryInfo.main_category,
            item: `https://cenvance.com/${categoryInfo.main_category_url_slug}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: seoData.breadcrumbName,
          },
        ],
      },
    };

    // Merge with additional schema if provided
    if (categorySEO?.additional_schema) {
      return { ...baseSchema, ...categorySEO.additional_schema };
    }

    return baseSchema;
  };

  const setSelectedCategory = (item) => {
    const selectedCategory = categories.find(
      (cat) => cat.id.toString() === item.key
    );
    setCurrentSelectedCategory(selectedCategory);
    setSelectedCategoryName(selectedCategory?.category);

    // Reset pagination when category changes
    setCurrentPage(1);
    setSubcategories([]);
    setHasMore(true);
    setLoadingMore(false);

    // Close drawer on mobile after selection
    if (isMobile) {
      setDrawerVisible(false);
    }

    navigate(`/${main_category_url}/${selectedCategory.url_slug}`);
  };

  // Scroll detection for infinite scroll with 70% threshold
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      const scrollPercentage =
        (scrollTop / (scrollHeight - clientHeight)) * 100;
      const shouldLoadMore = scrollPercentage >= 70;

      if (
        shouldLoadMore &&
        hasMore &&
        !loadingMore &&
        !subcategoryResponse.isLoading
      ) {
        setLoadingMore(true);
        setCurrentPage((prev) => prev + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore, subcategoryResponse.isLoading, currentPage]);

  // Load more subcategories when currentPage changes
  useEffect(() => {
    if (currentPage > 1 && category_url) {
      getSubcategoryRequest(
        "",
        API_METHODS.GET,
        `/website/category-page/${category_url}/subcategory?pageNumber=${currentPage}`
      );
    }
  }, [currentPage, category_url]);

  useEffect(() => {
    getCategoryRequest(
      "",
      API_METHODS.GET,
      `/website/category-page/${main_category_url}/category`
    );
    getBannerRequest(
      "",
      API_METHODS.GET,
      `/website/category-page/${main_category_url}/banner`
    );
  }, [main_category_url]);

  useEffect(() => {
    // Reset pagination when category changes
    setCurrentPage(1);
    setSubcategories([]);
    setHasMore(true);
    setLoadingMore(false);

    if (category_url) {
      getSubcategoryRequest(
        "",
        API_METHODS.GET,
        `/website/category-page/${category_url}/subcategory?pageNumber=1`
      );
    }
  }, [category_url]);

  useEffect(() => {
    const data =
      categoryResponse.data !== BLANK_VALUE ? categoryResponse.data : [];
    setCategories(data);
  }, [categoryResponse]);

  useEffect(() => {
    if (categories?.length > 1) {
      const selectedCategory = categories.find(
        (element) => element.url_slug === category_url
      );
      setCurrentSelectedCategory(selectedCategory);
      setSelectedCategoryName(selectedCategory?.category);
    }
  }, [categories, category_url]);

  useEffect(() => {
    const data = bannerResponse.data !== BLANK_VALUE ? bannerResponse.data : [];
    setBanner(data);
  }, [bannerResponse]);

  // Handle enhanced subcategory response with SEO data
  useEffect(() => {
    if (subcategoryResponse.data !== BLANK_VALUE && subcategoryResponse.data) {
      const responseData = subcategoryResponse.data;

      // Set category info and SEO data
      if (responseData.category_info) {
        setCategoryInfo(responseData.category_info);
      }
      if (responseData.category_seo) {
        setCategorySEO(responseData.category_seo);
      }

      // Handle subcategories data
      const newData = Array.isArray(responseData.subcategories)
        ? responseData.subcategories
        : [];
      const pagination =
        responseData.pagination !== BLANK_VALUE
          ? responseData.pagination
          : null;

      if (currentPage === 1) {
        setSubcategories(newData);
      } else if (newData.length > 0) {
        setSubcategories((prev) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          const newArray = Array.isArray(newData) ? newData : [];
          const existingIds = new Set(prevArray.map((item) => item.id));
          const uniqueNewData = newArray.filter(
            (item) => !existingIds.has(item.id)
          );

          const combined = [...prevArray, ...uniqueNewData];
          return combined;
        });
      }

      // Update hasMore based on pagination info
      if (pagination && pagination.maxPages) {
        const maxPages = parseInt(pagination.maxPages) || 1;
        const newHasMore = currentPage < maxPages;
        setHasMore(newHasMore);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    }
  }, [subcategoryResponse, currentPage]);

  // Generate SEO data
  const seoData = generateSEOData(categoryInfo, categorySEO);
  const schemaData = generateCategorySchema(categoryInfo, seoData);

  const CategoriesMenu = ({ style = {} }) => (
    <div style={style}>
      <Row
        style={{
          paddingLeft: isMobile ? 16 : 24,
          paddingRight: isMobile ? 16 : 0,
        }}
      >
        <h1
          style={{
            color: theme.colors.black,
            fontSize: isMobile ? 24 : 28,
            marginBottom: isMobile ? 16 : 24,
          }}
        >
          Categories
        </h1>
      </Row>
      <Divider style={{ margin: 0 }} />
      {categories && (
        <Menu
          mode="inline"
          defaultSelectedKeys={[categories[0]]}
          selectedKeys={[currentSelectedCategory?.id.toString()]}
          onSelect={(item) => {
            setSelectedCategory(item);
          }}
          style={{
            border: "none",
            fontSize: isMobile ? 14 : 16,
          }}
        >
          {categories?.map((category) => (
            <Menu.Item
              key={category.id}
              style={{
                padding: isMobile ? "12px 16px" : "8px 24px",
                height: "auto",
              }}
            >
              {category.category}
            </Menu.Item>
          ))}
        </Menu>
      )}
    </div>
  );
  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {seoData.keywords && (
          <meta name="keywords" content={seoData.keywords} />
        )}
        <meta name="robots" content={seoData.robots} />

        {/* Canonical URL */}
        <link rel="canonical" href={seoData.canonicalUrl} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={seoData.ogTitle} />
        <meta property="og:description" content={seoData.ogDescription} />
        <meta property="og:image" content={seoData.ogImage} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:type" content="website" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.twitterTitle} />
        <meta name="twitter:description" content={seoData.twitterDescription} />
        <meta name="twitter:image" content={seoData.twitterImage} />

        {/* Structured Data */}
        {schemaData && (
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        )}
      </Helmet>

      {banner && (
        <>
          <Banner title={banner.title} banner_text={banner.banner_text} />

          <Breadcrumbs
            items={[
              {
                title: formatBreadcrumbText(main_category_url),
              },
              {
                title: formatBreadcrumbText(category_url),
              },
            ]}
          />
        </>
      )}

      <Layout
        style={{
          minHeight: "100vh",
          padding: isMobile ? 16 : isTabletOrMobile ? 30 : 60,
        }}
      >
        {/* Mobile Categories Button */}
        {isMobile && (
          <div
            style={{
              marginBottom: 16,
              padding: "0 8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2
              style={{
                color: theme.colors.black,
                margin: 0,
                fontSize: 20,
              }}
            >
              {selectedCategoryName || "Categories"}
            </h2>
            <Button
              type="primary"
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                backgroundColor: theme.colors.skyBlue,
              }}
            >
              Categories
            </Button>
          </div>
        )}

        {/* Mobile Drawer for Categories */}
        <Drawer
          title="Categories"
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={isMobile ? "80%" : 300}
          bodyStyle={{ padding: 0 }}
        >
          <CategoriesMenu />
        </Drawer>

        {/* Desktop/Tablet Sidebar */}
        {!isMobile && (
          <Sider
            title="Categories"
            width={isTabletOrMobile ? 250 : 300}
            style={{
              paddingBottom: 20,
              background: "#fff",
              overflow: "visible",
              borderTopLeftRadius: 20,
              borderBottomLeftRadius: 20,
              marginRight: 16,
            }}
            className="border-gray"
          >
            <CategoriesMenu />
          </Sider>
        )}

        <Layout style={{ flex: 1 }}>
          <Content
            style={{
              overflow: "hidden",
              padding: isMobile ? "16px 8px" : "16px",
              paddingBottom: 20,
              borderTopRightRadius: isMobile ? 20 : 20,
              borderBottomRightRadius: isMobile ? 20 : 20,
              borderTopLeftRadius: isMobile ? 20 : 0,
              borderBottomLeftRadius: isMobile ? 20 : 0,
              background: "#fff",
            }}
            className="border-gray"
          >
            {!isMobile && (
              <Row style={{ paddingBottom: 24, paddingTop: 6 }}>
                <h1
                  style={{
                    color: theme.colors.black,
                    fontSize: isTabletOrMobile ? 24 : 28,
                  }}
                >
                  {selectedCategoryName}
                </h1>
              </Row>
            )}

            <Row gutter={[isMobile ? 8 : 16, isMobile ? 16 : 16]}>
              {subcategories?.map((subcategory, index) => (
                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  lg={8}
                  xl={6}
                  key={subcategory.id}
                  onClick={(event) => subcategoryClicked(subcategory, event)}
                >
                  <Card
                    hoverable
                    style={{
                      width: "100%",
                      cursor: "pointer",
                    }}
                    className="border-gray"
                    cover={
                      <img
                        alt={subcategory.subcategory}
                        src={
                          subcategory.image
                            ? subcategory.image
                            : productDemoImage
                        }
                        style={{
                          height: isMobile ? 200 : 300,
                          objectFit: "contain",
                        }}
                      />
                    }
                    bodyStyle={{
                      padding: isMobile ? 12 : 16,
                    }}
                  >
                    <Card.Meta
                      title={
                        <span
                          style={{
                            fontSize: isMobile ? 14 : 16,
                            fontWeight: "bold",
                          }}
                        >
                          {subcategory.subcategory}
                        </span>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Loading indicator for infinite scroll */}
            {loadingMore && (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  marginTop: "16px",
                }}
              >
                <Spin size="large" />
                <p style={{ marginTop: "8px", color: "#666" }}>
                  Loading more subcategories...
                </p>
              </div>
            )}

            {/* Empty state for subcategories */}
            {subcategories &&
              subcategories.length === 0 &&
              !subcategoryResponse.isLoading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "#666",
                  }}
                >
                  <p>No subcategories found for this category.</p>
                </div>
              )}
          </Content>
        </Layout>
      </Layout>
      <Footer isTabletOrMobile={isTabletOrMobile} />
    </div>
  );
}

export default CategoryPage;
